import { useCallback, useMemo } from 'react';
import { type UseFieldArrayUpdate, useFormContext, useWatch } from 'react-hook-form';
import { MatchingOperator } from '../../../../../../modules/audiences/AudienceForm/constants';
import { useNormalizedDefinitionValidation } from '../../../../../../modules/audiences/AudienceForm/hooks/useNormalizedDefinitionValidation';
import type {
  AudienceForm,
  FormSegment,
  GroupFieldName,
  GroupsPath,
} from '../../../../../../modules/audiences/AudienceForm/types';
import { segmentsToRangeGroups } from '../components/expandableSections/demographics/demographicsUtils';
import { isGroup } from '../components/utils/guards';
import { getGroupFromPath } from '../utils/getGroupFromPath';

export type UseDemographicsSectionProps = {
  groupFieldName: GroupFieldName;
  update: UseFieldArrayUpdate<AudienceForm, GroupsPath>;
  index: number;
};

export type UseDemographicsSectionResponse = {
  clearAllDemographics: (event?: React.MouseEvent<HTMLButtonElement>) => void;
  ageSegments: FormSegment[];
  genderSegments: FormSegment[];
  setDemographicAgeSegments: (segments: FormSegment[] | ((prev: FormSegment[]) => FormSegment[])) => void;
  setDemographicGenderSegments: (segments: FormSegment[] | ((prev: FormSegment[]) => FormSegment[])) => void;
  ageRanges: string;
  genderText: string;
  numberOfAgeAndGenderRangesSelected: number;
};

export const useDemographicsSection = ({
  groupFieldName,
  update,
  index,
}: UseDemographicsSectionProps): UseDemographicsSectionResponse => {
  const { control } = useFormContext<AudienceForm>();
  const { withValidation } = useNormalizedDefinitionValidation();

  const updateWithValidation = useCallback(withValidation(update), []);

  const observableNormalizedDefinition = useWatch({
    name: 'normalized_definition',
    control,
  });

  const watchedGroup = useMemo(
    () => getGroupFromPath(observableNormalizedDefinition, groupFieldName),
    [observableNormalizedDefinition, groupFieldName]
  );

  const ageSegments: FormSegment[] = useMemo(() => {
    if (!isGroup(watchedGroup)) return [];

    return watchedGroup.demographic_age?.segments ?? [];
  }, [watchedGroup]);

  const genderSegments: FormSegment[] = useMemo(() => {
    if (!isGroup(watchedGroup)) return [];

    return watchedGroup.demographic_gender?.segments ?? [];
  }, [watchedGroup]);

  const demographicAgeSegments: FormSegment[] = useMemo(() => {
    if (!isGroup(watchedGroup)) return [];

    return watchedGroup.demographic_age?.segments ?? [];
  }, [watchedGroup]);

  const demographicGenderSegments: FormSegment[] = useMemo(() => {
    if (!isGroup(watchedGroup)) return [];

    return watchedGroup.demographic_gender?.segments ?? [];
  }, [watchedGroup]);

  const setDemographicAgeSegments = useCallback(
    (segments: FormSegment[] | ((prev: FormSegment[]) => FormSegment[])) => {
      const newSegments = typeof segments === 'function' ? segments(demographicAgeSegments) : segments;
      const currentGroup = watchedGroup;

      if (!isGroup(currentGroup)) return;

      updateWithValidation(index, {
        ...currentGroup,
        demographic_age: {
          segments: newSegments,
          operator: MatchingOperator.UNION,
        },
      });
    },
    [demographicAgeSegments, updateWithValidation, index, watchedGroup]
  );

  const setDemographicGenderSegments = useCallback(
    (segments: FormSegment[] | ((prev: FormSegment[]) => FormSegment[])) => {
      const newSegments = typeof segments === 'function' ? segments(demographicGenderSegments) : segments;
      const currentGroup = watchedGroup;

      if (!isGroup(currentGroup)) return;

      updateWithValidation(index, {
        ...currentGroup,
        demographic_gender: {
          segments: newSegments,
          operator: MatchingOperator.UNION,
        },
      });
    },
    [demographicGenderSegments, updateWithValidation, index, watchedGroup]
  );

  const ageRanges: string = useMemo(() => {
    const ranges = segmentsToRangeGroups(ageSegments);
    if (ranges.length === 0) return '0';

    return ranges.map(range => `${range.min}-${range.max}`).join(', ');
  }, [ageSegments]);

  const genderText: string = useMemo(() => {
    if (!genderSegments.length) return 'None';

    return genderSegments.map(segment => segment.name).join(', ');
  }, [genderSegments]);

  const numberOfAgeAndGenderRangesSelected = useMemo(
    () => segmentsToRangeGroups(ageSegments).length + genderSegments.length,
    [genderSegments, ageSegments]
  );

  const clearAllDemographics = useCallback(
    (event?: React.MouseEvent<HTMLButtonElement>) => {
      event?.stopPropagation();

      const currentGroup = watchedGroup;

      if (!isGroup(currentGroup)) return;

      updateWithValidation(index, {
        ...currentGroup,
        demographic_age: { segments: [], operator: MatchingOperator.UNION },
        demographic_gender: { segments: [], operator: MatchingOperator.UNION },
      });
    },
    [updateWithValidation, index, watchedGroup]
  );

  return {
    clearAllDemographics,
    ageSegments,
    genderSegments,
    setDemographicAgeSegments,
    setDemographicGenderSegments,
    ageRanges,
    genderText,
    numberOfAgeAndGenderRangesSelected,
  };
};
